// --- File: TacticsBoard.tsx ---
import React, { useEffect, useState, useRef } from 'react';
import {
  getDocs,
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import Draggable from 'react-draggable';
import {
  getInitials,
  getPositionGroup,
  getSuitabilityColor,
  Player,
  Position,
} from '../players/utils/playerUtils';
import { formations, FormationOption } from './formations';
import TacticsPanel from './TacticsPanel';
import ConfirmModal from '../ConfirmModal';
import { usePageTitle } from '../../hooks/usePageTitle';

const pitchImage = '/images/pitch-plan.png';

const TacticsBoard: React.FC = () => {
  usePageTitle('Tactics | From the Pitchside');
  const { user } = useAuthContext();
  const [players, setPlayers] = useState<Player[]>([]);
  const [positions, setPositions] = useState<Position[]>(
    formations[0].positions
  );
  const [selectedFormation, setSelectedFormation] = useState<FormationOption>(
    formations[0]
  );
  const [selectedPosId, setSelectedPosId] = useState<string | null>(null);
  const [savedTactics, setSavedTactics] = useState<any[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const [pendingLoad, setPendingLoad] = useState<any | null>(null);

  const pitchRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const wasDraggedRef = useRef(false);

  useEffect(() => {
    const element = pitchRef.current;
    if (!element) return;

    const updateSize = () => {
      setSize({ width: element.offsetWidth, height: element.offsetHeight });
    };

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    updateSize();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchPlayers = async () => {
      if (!user?.teamId) return;
      const snap = await getDocs(
        collection(db, `teams/${user.teamId}/players`)
      );
      const data = snap.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Player)
      );
      setPlayers(data);
    };
    fetchPlayers();
  }, [user?.teamId]);

  useEffect(() => {
    if (!user?.teamId) return;
    const unsub = onSnapshot(
      collection(db, `teams/${user.teamId}/tactics`),
      snap => {
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSavedTactics(data);
      }
    );
    return () => unsub();
  }, [user?.teamId]);

  const selectedPos = positions.find(p => p.id === selectedPosId);
  const usedPlayerIds = positions
    .map(p => p.assigned?.playerId)
    .filter(Boolean);

  const assignPlayerToPosition = (posId: string, player: Player) => {
    const role = positions.find(p => p.id === posId)?.role || '';
    const group = getPositionGroup(role);
    const color = getSuitabilityColor(
      player.positions,
      group,
      selectedPos?.role
    );
    const initials = getInitials(`${player.firstName} ${player.lastName}`);

    setPositions(prev =>
      prev.map(pos =>
        pos.id === posId
          ? { ...pos, assigned: { playerId: player.id, initials, color } }
          : pos
      )
    );
    setSelectedPosId(null);
  };

  const clearPlayerFromPosition = (posId: string) => {
    setPositions(prev =>
      prev.map(pos =>
        pos.id === posId ? { ...pos, assigned: undefined } : pos
      )
    );
    setSelectedPosId(null);
  };

  const handleStop = (data: any, id: string) => {
    const newXPercent = (data.x / size.width) * 100;
    const newYPercent = (data.y / size.height) * 100;

    setPositions(prev =>
      prev.map(pos =>
        pos.id === id ? { ...pos, x: newXPercent, y: newYPercent } : pos
      )
    );
  };

  const handleFormationChange = (id: string) => {
    const newFormation = formations.find(f => f.id === id);
    if (!newFormation) return;

    const assignedPlayers: Set<string> = new Set();
    const updatedPositions = newFormation.positions.map(newPos => {
      const prev = positions.find(p => p.id === newPos.id && p.assigned);
      if (prev?.assigned && !assignedPlayers.has(prev.assigned.playerId)) {
        assignedPlayers.add(prev.assigned.playerId);
        return { ...newPos, assigned: prev.assigned };
      }
      return { ...newPos };
    });

    setSelectedFormation(newFormation);
    setPositions(updatedPositions);
  };

  const handleSaveTactic = async (name: string, overwriteId?: string) => {
    if (!user?.teamId) return;

    const tacticData = {
      name,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      formationId: selectedFormation.id,
      positions,
    };

    try {
      if (overwriteId) {
        const docRef = doc(db, `teams/${user.teamId}/tactics/${overwriteId}`);
        await setDoc(docRef, tacticData, { merge: true });
        setShowPanel(false);
        alert('Tactic overwritten successfully');
      } else {
        await addDoc(
          collection(db, `teams/${user.teamId}/tactics`),
          tacticData
        );
        setShowPanel(false);
        alert('Tactic saved successfully');
      }
    } catch (err) {
      console.error('Error saving tactic:', err);
      alert('Failed to save tactic.');
    }
  };

  const handleLoadTactic = (tactic: any) => {
    const formation = formations.find(f => f.id === tactic.formationId);
    if (formation) setSelectedFormation(formation);
    setPositions(tactic.positions || []);
    setShowPanel(false);
  };

  const handleDeleteTactic = async (tactic: any) => {
    if (!user?.teamId) return;
    try {
      await deleteDoc(doc(db, `teams/${user.teamId}/tactics/${tactic.id}`));
    } catch (err) {
      console.error('Error deleting tactic:', err);
      alert('Failed to delete tactic.');
    }
  };

  return (
    <div
      className="w-full max-w-4xl mx-auto relative border rounded overflow-hidden"
      style={{ height: 'calc(100vh - 70px)' }}
    >
      <div className="absolute top-4 left-4 z-40 bg-white rounded shadow p-2">
        <select
          value={selectedFormation.id}
          onChange={e => handleFormationChange(e.target.value)}
          className="text-sm border rounded px-2 py-1"
        >
          {formations.map(f => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div
        ref={pitchRef}
        className="relative w-full max-w-4xl mx-auto bg-no-repeat bg-center bg-contain"
        style={{ paddingTop: '120%', backgroundImage: `url(${pitchImage})` }}
      >
        <div className="absolute inset-0">
          {size.width > 0 &&
            size.height > 0 &&
            positions.map(pos => (
              <Draggable
                key={pos.id}
                bounds="parent"
                position={{
                  x: (pos.x / 100) * size.width,
                  y: (pos.y / 100) * size.height,
                }}
                onStart={() => {
                  wasDraggedRef.current = false;
                }}
                onDrag={() => {
                  wasDraggedRef.current = true;
                }}
                onStop={(e, data) => handleStop(data, pos.id)}
              >
                <div
                  onPointerUp={() => {
                    if (!wasDraggedRef.current) {
                      setSelectedPosId(pos.id);
                    }
                  }}
                  className="absolute z-20"
                  style={{
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'auto',
                  }}
                >
                  <div className="relative w-10">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs border"
                      style={{
                        backgroundColor:
                          pos.assigned?.color || 'rgba(0,0,0,0.6)',
                      }}
                    >
                      {pos.assigned
                        ? players.find(p => p.id === pos.assigned?.playerId)
                            ?.shirtNumber ?? pos.role
                        : pos.role}
                    </div>
                    {pos.assigned && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 text-[14px] text-white mt-1 px-1 rounded"
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          top: '100%',
                        }}
                      >
                        {
                          players.find(p => p.id === pos.assigned?.playerId)
                            ?.firstName
                        }{' '}
                        {
                          players.find(p => p.id === pos.assigned?.playerId)
                            ?.lastName
                        }
                      </div>
                    )}
                  </div>
                </div>
              </Draggable>
            ))}
        </div>

        {selectedPosId && selectedPos && (
          <div className="absolute top-4 left-4 right-4 bg-white rounded shadow-lg p-4 z-50">
            <h3 className="text-lg font-bold mb-2">
              Assign Player to {selectedPos?.role}
            </h3>

            <div className="mb-4 text-sm">
              <p className="mb-1 font-semibold">Suitability Legend:</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full inline-block"
                    style={{ backgroundColor: '#003366' }}
                  ></span>
                  <span>Exact position match</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block bg-orange-500"></span>
                  <span>Same position group</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block bg-red-600"></span>
                  <span>Out of position</span>
                </li>
              </ul>
            </div>

            <div className="max-h-[20vh] overflow-y-auto grid grid-cols-2 gap-2 mb-4">
              {players.map(player => {
                const alreadyUsed = usedPlayerIds.includes(player.id);
                const group = getPositionGroup(selectedPos?.role || '');
                const color = getSuitabilityColor(
                  player.positions,
                  group,
                  selectedPos?.role
                );
                const initials = getInitials(
                  `${player.firstName} ${player.lastName}`
                );

                return (
                  <button
                    key={player.id}
                    disabled={alreadyUsed}
                    onClick={() =>
                      assignPlayerToPosition(selectedPosId, player)
                    }
                    className={`w-full p-2 rounded text-white font-bold ${
                      alreadyUsed ? 'bg-gray-300 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: alreadyUsed ? '#ccc' : color }}
                  >
                    {initials} – {player.firstName} {player.lastName}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => clearPlayerFromPosition(selectedPosId)}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Clear Position
              </button>
              <button
                onClick={() => setSelectedPosId(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="absolute bottom-4 right-4 flex gap-2 z-50">
          <button
            onClick={() => setShowPanel(true)}
            className="px-4 py-2 bg-blue-700 text-white rounded"
          >
            Tactics Manager
          </button>
          {showPanel && (
            <TacticsPanel
              show={showPanel}
              tactics={savedTactics}
              onClose={() => setShowPanel(false)}
              onLoad={handleLoadTactic}
              onSave={handleSaveTactic}
              onDelete={handleDeleteTactic}
            />
          )}
          {pendingLoad && (
            <ConfirmModal
              title="Load Tactic"
              message="Are you sure you want to load this tactic? Any unsaved changes will be lost."
              onConfirm={() => {
                handleLoadTactic(pendingLoad);
                setPendingLoad(null);
              }}
              onCancel={() => setPendingLoad(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TacticsBoard;